import React from "react";
import { Icon, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getLocationType, getWorkingHoursList } from "../../../../../services/display";
import { getVenueCategory, getVenueIcon, getVenueSubtypeLabel } from "../../../../../services/venueCategories";
import { SeparateNumberFormat } from "../../../../../services/utils";
import StatTabCard from "./StatTabCard";
import {
  getSizeText,
  getAdFormats,
  getRotationLabel,
  getApproxSpots,
  getRestrictions,
  getCostPerDay,
  getAudienceValues,
  formatMoney,
} from "../../utils/screenDetailData";

// icon: antd icon type, or a ready-made node.
const InfoItem = ({ icon, label, value, info }) => (
  <div className="screenInfo__item">
    <div className="screenInfo__top">
      <span className="screenInfo__icon">
        {typeof icon === "string" ? <Icon type={icon} /> : icon}
      </span>
      {info && (
        <Tooltip title={info} placement="topRight">
          <Icon type="info-circle" className="screenInfo__info" />
        </Tooltip>
      )}
    </div>
    <span className="screenInfo__label">{label}</span>
    <span className="screenInfo__value">{value}</span>
  </div>
);

const ScreenDetailsSection = ({ display, currency, isCpmi }) => {
  const { t } = useTranslation();

  let workingHoursList = null;
  if (display.working_hours && display.working_hours.length) {
    const list = [];
    getWorkingHoursList(t, display.working_hours, list);
    workingHoursList = <ul style={{ margin: 0, paddingLeft: 14 }}>{list}</ul>;
  }

  const cur = currency || display.price_currency || display.display_currency || "ARS";
  const cost = getCostPerDay(display) || 0;
  const aud = getAudienceValues(display);
  const subtypeLabel = getVenueSubtypeLabel(display, t);

  const periodTabs = [
    { key: "day", label: t("Daily") },
    { key: "week", label: t("Weekly") },
    { key: "month", label: t("Monthly") },
  ];

  const items = [
    { icon: "desktop", label: t("Type"), value: getLocationType(display.location_type, t) },
    {
      icon: "clock-circle",
      label: t("Ads time"),
      value: `${(display.slot_length || 0) / 1000} seg`,
      info: t("Length of ads time"),
    },
    ...(subtypeLabel
      ? [
          {
            icon: (
              <FontAwesomeIcon
                icon={getVenueIcon(display)}
                // Venue glyphs are padded in their viewBox, so they need more than the 20px antd icons.
                style={{ color: getVenueCategory(display).color, fontSize: 24 }}
              />
            ),
            label: t("Sub Type of screen"),
            value: subtypeLabel,
          },
        ]
      : []),
    // cpmi companies never see projected playouts
    ...(!isCpmi
      ? [
          {
            icon: "picture",
            label: t("Approximate impressions"),
            value: `${getApproxSpots(display)} / ${t("Day").toLowerCase()}`,
            info: t(
              "It is an approximate value, because it depends on the days and rules you choose in your campaign. Once the campaign is created, you will get the exact impressions."
            ),
          },
        ]
      : []),
    { icon: "column-width", label: t("Size"), value: getSizeText(display, t) },
    {
      icon: "calendar",
      label: t("Hours of Operation"),
      value: workingHoursList ? t("List of days") : t("Lit all day every day"),
      info: workingHoursList,
    },
    {
      icon: "arrows-alt",
      label: t("Resolution"),
      value: `${display.resolution_width}x${display.resolution_height}`,
    },
    { icon: "play-circle", label: t("Allowed ad format"), value: getAdFormats(display.format_settings, t) },
    { icon: "stop", label: t("Restrictions"), value: getRestrictions(display, t) },
    { icon: "reload", label: t("Ad rotated"), value: getRotationLabel(display.rotation, t) },
  ];

  return (
    <div className="screenDetail__section">
      <h3 className="screenDetail__section-title">{t("Screen details")}</h3>
      <div className="screenDetails">
        <div className="screenDetails__info">
          {items.map((it, i) => (
            <InfoItem key={i} {...it} />
          ))}
        </div>
        <div className="screenDetails__side">
          <StatTabCard
            icon="eye"
            label={t("Audience")}
            tabs={periodTabs}
            values={aud}
            format={(v) => SeparateNumberFormat(v)}
          />
          <StatTabCard
            icon="dollar"
            label={t("Cost")}
            tabs={periodTabs}
            values={{ day: cost, week: cost * 7, month: cost * 30 }}
            format={(v) => `${formatMoney(v, cur)} ${cur}`}
          />
        </div>
      </div>
    </div>
  );
};

export default ScreenDetailsSection;
